<template>
  <section class="page-interaction boho-earn earn-page">
    <b-overlay :show="loading" no-center variant="transparent" rounded="sm">
      <template #overlay>
        <loading-spinner />
      </template>
      <nav class="as-tabs">
        <RouterLink
          :to="{name: mobile ? 'account-status-mobile' : 'account-status'}"
          class="as-tab"
        >
          {{ $t('Account Status') }}
        </RouterLink>
        <RouterLink
          :to="{name: mobile ? 'gains-mobile' : 'gains'}"
          class="as-tab"
        >
          {{ $t('My Gains') }}
        </RouterLink>
        <RouterLink
          :to="{name: mobile ? 'cashouts-mobile' : 'cashouts'}"
          class="as-tab active"
        >
          {{ $t('Cashouts Page Title') }}
        </RouterLink>
      </nav>
      <b-row v-if="data">
        <b-col xl="5" lg="6" md="8" sm="12">
          <div class="earn-hero earn-hero--center d-flex justify-content-center align-items-center flex-column">
            <span class="fs-14 color-gray">
              {{ $t('Current Beans') }}
            </span>
            <span class="color-black fw-600 fs-32">
              <span>
                {{ numberFormatter(data.earnedCredits) }}
              </span>
            </span>
            <b-progress
              v-if="data.earnedCredits !== null && data.minCreditsForCashout"
              variant="success"
              :max="data.minCreditsForCashout"
              animated
              striped
              style="width: 100%; max-width: 500px"
              height="1rem"
            >
              <b-progress-bar :value="data.earnedCredits">
                <div class="reward-progress-bar">
                  {{ Math.min(Math.round((data.earnedCredits / data.minCreditsForCashout) * 100), 100) }}%
                </div>
              </b-progress-bar>
            </b-progress>
            <div
              v-if="data.earnedCredits < data.minCreditsForCashout"
              class="text-center"
            >
              <p class="fs-12 color-gray mb-0">
                {{ $t('Beans needed for cashout') }} :
                {{ numberFormatter(data.minCreditsForCashout) }}
              </p>
              <p
                v-if="data.minCreditsForCashout === 171000"
                class="fs-12 color-gray"
              >
                {{ $t('minCreditsForCashout171k') }}
              </p>
            </div>
            <span
              v-else class="fs-12 color-gray"
            >
              {{ $t('Cashout Deserved') }}
            </span>
          </div>
          <b-alert class="mt-1" :show="showSuccessMessage" dismissible variant="success" dismissa>
            <p class="p-1">
              {{ $t('Successful saving of information') }}
            </p>
          </b-alert>
          <h5
            class="earn-form-toggle cursor-pointer"
            @click="showForm = !showForm"
          >
            <feather-icon
              icon="EditIcon"
              size="16"
            />
            {{ $t('Create / Update Cashout Information') }}
          </h5>
          <validation-observer
            v-if="showForm"
            ref="gainRequestForm"
            #default="{invalid}"
            class="mb-1 earn-form-card"
          >
            <b-alert class="mt-1" show variant="info">
              <p class="p-1">
                {{ $t('create_update_cashout_information_text') }}
              </p>
            </b-alert>
            <b-form
              class="mt-2"
              @submit.prevent
            >
              <b-form-group
                v-if="!isTurkishUser"
                label-for="gainRequestType"
                :label="$t('Type')"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('Tip')"
                  vid="gainRequestType"
                  rules="required"
                >
                  <b-form-radio-group
                    id="gainRequestType"
                    v-model="gainRequest.type"
                    name="radio-sub-component"
                  >
                    <b-form-radio value="cr">
                      {{ $t('CR') }}
                    </b-form-radio>
                    <b-form-radio value="bank">
                      {{ $t('B') }}
                    </b-form-radio>
                  </b-form-radio-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                v-if="gainRequest.type === 'bank'"
                label-for="gainRequestAccountHolder"
                :label="$t('Account holder')"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('Account holder')"
                  vid="gainRequestAccountHolder"
                  rules="required|min:3"
                >
                  <b-form-input
                    id="gainRequestAccountHolder"
                    v-model="gainRequest.accountHolder"
                    :disabled="loading"
                    :placeholder="$t('Account holder')"
                    rows="3"
                    max-rows="6"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                v-if="gainRequest.type === 'bank'"
                label-for="gainRequestBankName"
                :label="$t('Bank name')"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('Bank name')"
                  vid="gainRequestBankName"
                  rules="required|min:3"
                >
                  <b-form-input
                    id="gainRequestBankName"
                    v-model="gainRequest.bankName"
                    :disabled="loading"
                    :placeholder="$t('Bank name')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                v-if="gainRequest.type === 'bank'"
                label-for="gainRequestIban"
                :label="$t('Account number')"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('Account number')"
                  vid="iban"
                  rules="required"
                >
                  <b-form-input
                    id="gainRequestIban"
                    v-model="gainRequest.iban"
                    :disabled="loading"
                    :placeholder="$t('Account number')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                v-if="false && gainRequest.type === 'cr'"
                label-for="gainRequestData"
                :label="$t('Other')"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('Other')"
                  vid="data"
                  rules="required|min:3"
                >
                  <b-form-textarea
                    id="gainRequestData"
                    v-model="gainRequest.data"
                    :disabled="loading"
                    :placeholder="$t('Other')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                v-if="gainRequest.type === 'cr'"
                label-for="gainRequestEmail"
                :label="$t('Email')"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('Email')"
                  vid="email"
                  rules="required|email"
                >
                  <b-form-input
                    id="gainRequestEmail"
                    v-model="gainRequest.email"
                    :disabled="loading"
                    :placeholder="$t('Email')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                v-if="gainRequest.type === 'cr'"
                label-for="gainRequestName"
                :label="$t('Name Surname')"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('Name Surname')"
                  vid="name"
                  rules="required"
                >
                  <b-form-input
                    id="gainRequestName"
                    v-model="gainRequest.name"
                    :disabled="loading"
                    :placeholder="$t('Name Surname')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <!-- TC alanı: sadece TR locale ve banka tipi için göster -->
              <b-form-group
                v-if="gainRequest.type === 'bank' && isTurkishUser"
                label-for="gainRequestTc"
                label="T.C."
              >
                <validation-provider
                  #default="{ errors }"
                  name="T.C."
                  vid="tc"
                  rules="required"
                >
                  <b-form-input
                    id="gainRequestTc"
                    v-model="gainRequest.tc"
                    :disabled="loading"
                    placeholder="T.C."
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-form-group
                v-if="gainRequest.type === 'cr'"
                label-for="gainRequestWallet"
                :label="$t('wallet_short')"
              >
                <validation-provider
                  #default="{ errors }"
                  :name="$t('wallet_short')"
                  vid="wallet"
                  rules="required"
                >
                  <b-form-input
                    id="gainRequestWallet"
                    v-model="gainRequest.wallet"
                    :disabled="loading"
                    :placeholder="$t('wallet_short')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
              <b-button
                variant="primary"
                class="mt-2 px-4"
                :disabled="loading"
                block
                @click="makeGainRequest"
              >
                <b-spinner v-if="loading" small></b-spinner>
                {{ $t('Save') }}
              </b-button>
            </b-form>
          </validation-observer>
        </b-col>
      </b-row>
      <b-row v-if="cashoutItems.length">
        <b-col xl="5" lg="6" md="8" sm="12">
          <b-table
            :items="cashoutItems"
            :busy="cashoutLoading"
            :fields="cashoutFields"
            class="mt-1"
            borderless
          >
            <template #table-busy>
              <div class="text-center text-danger my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong>{{ $t('Loading') }}...</strong>
              </div>
            </template>
            <template #cell(credits)="data">
              <span class="fs-16 fw-300 color-black">
                {{ numberFormatter(data.item.credits) }}
              </span>
            </template>
            <template #cell(createdAt)="data">
              <span class="fs-16 fw-300 color-black">
                {{ data.item.createdAt }}
              </span>
            </template>
            <template #cell(status)="data">
              <span
                v-if="data.item.status === 'requested'"
                class="fs-16 fw-600 color-black">
                {{ $t('Request sent') }}
              </span>
              <span
                v-else-if="data.item.status === 'rejected'"
                class="fs-16 fw-600" style="color: #ef584d">
                {{ $t('Rejected') }}
              </span>
              <span
                v-else-if="data.item.status === 'done'"
                class="fs-16 fw-600" style="color: #399a24">
                {{ $t('Done') }}
              </span>
              <span v-else
                    class="fs-16 fw-600" style="color: #e15b02">
                {{ $t(data.item.status) }}
              </span>
            </template>
          </b-table>
        </b-col>
      </b-row>
    </b-overlay>
  </section>
</template>

<script>
// freeCallUser
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BAlert,
  BButton,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormRadio,
  BFormRadioGroup,
  BFormTextarea,
  BOverlay,
  BRow,
  BSpinner,
  BTable,
  BNav,
  BNavItem,
  BProgressBar,
  BProgress,
} from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
import {
  showNotify,
  throwDefaultError,
  isValidIBANNumber,
  numberFormatter,
} from '@/helpers/helpers'
import moment from 'moment'
import _ from 'lodash'

// import { getUser } from '@/auth/utils'

const iconBean = require('@/assets/images/icons/bean.png')
const iconDiamond = require('@/assets/images/icons/diamonds-1.svg')
const iconPhone = require('@/assets/images/icons/phone-green.svg')

export default {
  name: 'CashoutsComponent',
  components: {
    BProgress,
    BProgressBar,
    BNavItem,
    BNav,
    BAlert,
    BButton,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormRadio,
    BFormRadioGroup,
    BFormTextarea,
    BOverlay,
    BRow,
    BSpinner,
    BTable,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    mobile: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTabIndex: 0,
      cashoutLoading: false,
      cashouts: [],
      data: null,
      gainRequest: {
        type: 'cr',
        accountHolder: '',
        bankName: '',
        iban: '',
        data: '',
        email: '',
        name: '',
        wallet: '',
        tc: '',
      },
      iconBean,
      iconDiamond,
      iconPhone,
      limit: 300,
      loading: false,
      showForm: false,
      showSuccessMessage: false,
      streamStatistics: null,
    }
  },
  watch: {
    'gainRequest.type'() {
      // Sadece Non-TR kullanıcılar tip değiştirdiğinde formu resetle
      if (!this.isTurkishUser) {
        this.$nextTick(() => {
          if (this.$refs.gainRequestForm) {
            this.$refs.gainRequestForm.reset()
          }
        })
      }
    },
    isTurkishUser(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.gainRequest.type = 'bank'
      }
    },
  },
  computed: {
    isTurkishUser() {
      return this.$store.getters['auth/userCountry'] === 'TR'
    },
    cashoutFields() {
      return [
        { key: 'credits', label: this.$t('Beans won') },
        { key: 'createdAt', label: this.$t('Date') },
        { key: 'status', label: this.$t('Status') },
      ]
    },
    cashoutItems() {
      const cashoutsData = this.cashouts.map(cashout => {
        return {
          credits: cashout.credits,
          status: cashout.status,
          createdAt: this.getDate('2024-11-30'),
        }
      })

      return [
        // {
        //   credits: this.data && this.data.earnedCredits ? this.data.earnedCredits : 0,
        //   status: this.data && this.data.earnedCredits < this.data.minCreditsForCashout ? 'Minimum limit pending' : 'pending',
        //   createdAt: `${moment()
        //     .add(1, 'week')
        //     .startOf('isoWeek')
        //     .subtract(1, 'day')
        //     .format('DD.MM.YYYY')} 23:59`,
        // },
        ...cashoutsData,
      ]
    },
  },
  created() {
    if (this.isTurkishUser) {
      this.gainRequest.type = 'bank'
    }
    this.getGains()
    this.getCashouts()
  },
  methods: {
    numberFormatter,
    isValidTurkishTc(tc) {
      const cleaned = (tc || '').replace(/\D/g, '')
      if (cleaned.length !== 11) return false
      if (cleaned[0] === '0') return false

      const digits = cleaned.split('').map(d => parseInt(d, 10))
      const [
        d1, d2, d3, d4, d5,
        d6, d7, d8, d9, d10, d11,
      ] = digits

      const sumOdd = d1 + d3 + d5 + d7 + d9
      const sumEven = d2 + d4 + d6 + d8
      const check10 = ((sumOdd * 7) - sumEven) % 10
      const check11 = (digits.slice(0, 10).reduce((acc, v) => acc + v, 0)) % 10

      return check10 === d10 && check11 === d11
    },
    getGains() {
      this.loading = true
      useJwt.accountCredits()
        .then(response => {
          this.data = response.data.body
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => { this.loading = false })
    },
    getCashouts() {
      this.cashoutLoading = true
      useJwt.getCashouts()
        .then(response => {
          this.cashouts = response.data.body
        })
        .catch(error => {
          throwDefaultError(this.$toast, error)
        })
        .finally(() => { this.cashoutLoading = false })
    },
    getDate(date) {
      return moment(date).format('DD.MM.YYYY HH:mm:ss')
    },
    makeGainRequest() {
      this.$refs.gainRequestForm.validate().then(success => {
        if (success) {
          // Mevcut Banka/IBAN kontrolü
          if (this.gainRequest.type === 'bank' && !!this.gainRequest.iban && !isValidIBANNumber(this.gainRequest.iban)) {
            this.$refs.gainRequestForm.setErrors({
              iban: this.$t('Please enter a valid format'),
            })
            return
          }

          // TC Kimlik kontrolü (sadece TR kullanıcı ve banka için)
          if (this.gainRequest.type === 'bank' && this.isTurkishUser) {
            if (!this.isValidTurkishTc(this.gainRequest.tc)) {
              this.$refs.gainRequestForm.setErrors({
                tc: this.$t('Please enter a valid format'),
              })
              return
            }
          }

          // --- YENİ EKLENEN KISIM BAŞLANGIÇ ---
          if (this.gainRequest.type === 'cr') {
            // 1. İsim Kontrolü: En az 2 kelime olmalı
            const nameValue = (this.gainRequest.name || '').trim()
            if (nameValue.split(/\s+/).length < 2) {
              this.$refs.gainRequestForm.setErrors({
                name: this.$t('Please enter at least 2 words'), // "Lütfen en az 2 kelime giriniz"
              })
              return
            }

            // 2. Cüzdan Kontrolü: TRON (T ile başlamalı, 30 karakter olmalı)
            const walletValue = this.gainRequest.wallet || ''
            if (!walletValue.startsWith('T') || walletValue.length <= 30) {
              this.$refs.gainRequestForm.setErrors({
                wallet: this.$t('Please enter a valid format'),
              })
              return
            }
          }
          // --- YENİ EKLENEN KISIM BİTİŞ ---

          const hasPapara = Object.values(this.gainRequest).some(value => {
            if (typeof value === 'string') {
              return value.toLowerCase().includes('papara')
            }
            return false
          })

          if (hasPapara) {
            return
          }
          this.loading = true

          let payload = {}

          if (this.gainRequest.type === 'bank') {
            payload = {
              cr: 0,
              accountHolder: this.gainRequest.accountHolder,
              bankName: this.gainRequest.bankName,
              iban: this.gainRequest.iban,
              tc: this.gainRequest.tc,
            }
          } else {
            payload = {
              cr: 1,
              accountHolder: this.gainRequest.name,
              bankName: this.gainRequest.email,
              iban: this.gainRequest.wallet,
              data: 'cr',
            }
          }

          useJwt.postPaymentsUserCashoutDetails(payload)
            .then(() => {
              this.gainRequest.type = this.isTurkishUser ? 'bank' : 'cr'
              this.gainRequest.accountHolder = ''
              this.gainRequest.bankName = ''
              this.gainRequest.iban = ''
              this.gainRequest.data = ''
              this.gainRequest.name = ''
              this.gainRequest.wallet = ''
              this.gainRequest.email = ''
              this.gainRequest.tc = ''
              this.$refs.gainRequestForm.reset()
              this.showForm = false
              this.getCashouts()
              this.getGains()
              showNotify(this.$toast, `${this.$t('Successful saving of information')}.`, 'success')
              this.showSuccessMessage = true
            })
            .catch(error => {
              throwDefaultError(this.$toast, error)
            })
            .finally(() => { this.loading = false })
        }
      })
    },
    timeLeft(time) {
      if (time <= 0) return '0'

      let result = ''
      let totalSeconds = time

      const days = Math.floor(totalSeconds / (60 * 60 * 24))
      totalSeconds -= days * (60 * 60 * 24)

      const hours = Math.floor(totalSeconds / (60 * 60))
      totalSeconds -= hours * (60 * 60)

      const minutes = Math.floor(totalSeconds / (60))
      totalSeconds -= minutes * 60

      const seconds = Math.floor(totalSeconds % 60)

      // if (days > 0) {
      //   result += days + ` ${this.$t('days')}, `
      // }

      if (hours > 0) {
        result += `${hours} ${this.$t('hours')}, `
      }

      if (minutes > 0) {
        result += `${minutes} ${this.$t('minutes')}, `
      }

      if (seconds > 0) {
        result += `${seconds} ${this.$t('seconds')}, `
      }

      return _.trimEnd(result, ', ')
    },
  },
}
</script>

<style lang="scss" scoped>
.nav-link {
  a {
    font-size: 13px !important;
  }
  &.active {
    a {
      color: #ffffff !important;
    }
  }
}
.nav-pills .nav-link {
  padding: 0.786rem 0.35rem !important;
}
</style>

<style>
.table th, .table td {
  padding: 0.72rem 1rem !important;
}
</style>

<style lang="scss">
@import '~@/assets/scss/boho-earnings.scss';
</style>

<route lang="yaml">
meta:
  navActiveLink: account-status
</route>
